import { Link } from 'react-router-dom'
import AppDropdown from '../../../components/AppDropdown'
import AppDateInput from '../../../components/AppDateInput'
import AppPagination from '../../../components/AppPagination'
import SharedFilters from '../../../components/SharedFilters'
import DateModifiedIcon from '../../../components/DateModifiedIcon'
import { useTimesheetListScreen } from './hooks/useTimesheetListScreen'
import { formatHoursAndMinutes } from '../../../utils/formatDuration'

function statusChipClass(status) {
  if (status === 'APPROVED') return 'bg-emerald-100 text-emerald-700'
  if (status === 'REJECTED') return 'bg-error-container text-error'
  return 'bg-primary-fixed text-primary'
}

export default function TimesheetListScreen() {
  const { state, actions } = useTimesheetListScreen()

  return (
    <div className="mx-auto w-full max-w-[1400px] pb-10">
      <header className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="rounded-[2rem] bg-surface-container-low px-8 py-8">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-primary">Execution Finance and Utilization</p>
          <h1 className="mt-3 text-4xl font-extrabold tracking-tight text-on-surface md:text-5xl">Timesheet List</h1>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-on-surface-variant md:text-lg">
            View, manage, and approve logged hours across projects and jobs. Ensure entries are accurate for billing and utilization tracking.
          </p>
        </div>
      </header>

      <div className="mb-6">
        <SharedFilters
          searchField={state.searchField}
          setSearchField={actions.setSearchField}
          searchFieldOptions={state.searchFieldOptions}
          searchText={state.searchText}
          setSearchText={actions.setSearchText}
          statusFilter={state.approvalStatus}
          setStatusFilter={actions.setApprovalStatus}
          statusOptions={state.statusOptions}
          projectFilter={state.projectFilter}
          setProjectFilter={actions.setProjectFilter}
          projectOptions={state.projectOptions}
          taskFilter={state.taskFilter}
          setTaskFilter={actions.setTaskFilter}
          taskOptions={state.taskOptions}
          jobFilter={state.jobFilter}
          setJobFilter={actions.setJobFilter}
          jobOptions={state.jobOptions}
          agentFilter={state.userFilter}
          setAgentFilter={actions.setUserFilter}
          agentOptions={state.userOptions}
          companyFilter={state.companyFilter}
          setCompanyFilter={actions.setCompanyFilter}
          companyOptions={state.companyOptions}
          branchFilter={state.branchFilter}
          setBranchFilter={actions.setBranchFilter}
          branchOptions={state.branchOptions}
          departmentFilter={state.departmentFilter}
          setDepartmentFilter={actions.setDepartmentFilter}
          departmentOptions={state.departmentOptions}
          fromDate={state.fromDate}
          setFromDate={actions.setFromDate}
          toDate={state.toDate}
          setToDate={actions.setToDate}
          onReset={actions.onReset}
          visibleAccordionFilters={[
            'project',
            'task',
            'job',
            'company',
            'branch',
            'department',
            'fromDate',
            'toDate',
          ]}
        />
      </div>

      {state.error ? (
        <div className="mb-4 rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">{state.error}</div>
      ) : null}

      <section className="overflow-hidden rounded-3xl bg-surface-container-low">
        {/* <div className="px-6 py-5">
          <span className="inline-flex rounded-full bg-secondary-container px-4 py-2 text-base font-semibold text-on-secondary-container">
            Showing {state.rows.length} entries
          </span>
        </div> */}

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1180px] border-separate border-spacing-0 text-left">
            <thead>
              <tr className="bg-surface-container-highest/30">
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Timesheet No</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Date</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Project</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Job</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Hours Logged</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Approval</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/20">
              {state.isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-16 text-center text-sm text-on-surface-variant">
                    Loading timesheets...
                  </td>
                </tr>
              ) : state.rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-16 text-center text-sm text-on-surface-variant">
                    No timesheet entries found.
                  </td>
                </tr>
              ) : (
                state.rows.map((row) => (
                  <tr key={row.id} className="transition-colors hover:bg-surface-container-lowest">
                    <td className="px-6 py-5 text-base font-semibold text-on-surface">{row.timesheetNo}</td>
                    <td className="px-6 py-5 text-base text-on-surface">
                      <div className="flex items-center gap-1.5">
                        <span>{row.entryDate}</span>
                        <DateModifiedIcon
                          hasDateChanges={row.hasDateChanges}
                          module="TIMESHEET"
                          entityId={row.id}
                        />
                      </div>
                    </td>
                    <td className="px-6 py-5 text-base font-semibold text-on-surface">{row.projectName}</td>
                    <td className="px-6 py-5 text-base text-on-surface">{row.jobName}</td>
                    <td className="px-6 py-5 text-base font-bold text-on-surface">{formatHoursAndMinutes(row.hoursLogged)}</td>
                    <td className="px-6 py-5">
                      <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider ${statusChipClass(row.approvalStatus)}`}>
                        {row.approvalStatus}
                      </span>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/nerve/timesheet/entry?id=${row.id}&mode=view`}
                          state={{ returnTo: `/nerve/timesheet/list${window.location.search}` }}
                          className="rounded-full bg-primary-fixed p-2 text-primary"
                          title="Preview"
                        >
                          <span className="material-symbols-outlined text-base">visibility</span>
                        </Link>
                        <Link
                          to={`/nerve/timesheet/entry?id=${row.id}`}
                          state={{ returnTo: `/nerve/timesheet/list${window.location.search}` }}
                          className="rounded-full bg-primary-fixed p-2 text-primary"
                          title="Edit"
                        >
                          <span className="material-symbols-outlined text-base">edit</span>
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-outline-variant/20 px-6 py-5">
          <span className="text-sm text-on-surface-variant">
            Showing {state.rangeStart}-{state.rangeEnd} of {state.totalCount} timesheet entries
          </span>

          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.goToPage}
            onPrevious={actions.goToPreviousPage}
            onNext={actions.goToNextPage}
            canPrevious={state.page > 1}
            canNext={Boolean(state.hasMore) || state.page < state.totalPages}
          />
        </div>
      </section>

      <section className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
        <article className="rounded-2xl bg-surface-container-low p-6">
          <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">Pending Approval</p>
          <h3 className="mt-2 text-4xl font-black text-on-surface">{state.approvalSummary.pending}</h3>
        </article>
        <article className="rounded-2xl bg-surface-container-low p-6">
          <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">Approved</p>
          <h3 className="mt-2 text-4xl font-black text-on-surface">{state.approvalSummary.approved}</h3>
        </article>
        <article className="rounded-2xl bg-primary p-6 text-on-primary">
          <p className="text-xs font-bold uppercase tracking-widest text-primary-fixed-dim">Total Logged (Current View)</p>
          <h3 className="mt-2 text-4xl font-black">{formatHoursAndMinutes(state.approvalSummary.totalHours)}</h3>
        </article>
      </section>
    </div>
  )
}
